// V14.9 — Fullscreen scrollable detail view for a single news article.
// Re-renders the saved block format (text + [IMAGE: caption] markers) into
// rich, readable long-form. Used by:
//   • MPNewsPostsPage (admin grid)
//   • MPDashboardPage (overview cards)
//   • MPExerciseDashboard (per-exercise feed)
//   • MPPublicDisplayPage (Live Display kiosk)
//   • MPGuestPublicDisplayPage (Guest view)
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Calendar, MapPin, User, Tag, Newspaper } from 'lucide-react';
import { useMediaLightbox, LongPressMediaTile } from './MediaLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;

const NEWS_PLATFORM_COLORS = {
  bbc: '#BB1919', cnn: '#CC0000', reuters: '#FF8000', aljazeera: '#D4AA00',
  nytimes: '#000000', guardian: '#052962', foxnews: '#003366', rt: '#5EAA22',
  xinhua: '#D52B1E',
};

// Parse stored content + media_urls into renderable blocks.
// We mirror payloadToBlocks() from NewsArticleFormDialog so editor and reader stay aligned.
const parseBlocks = (content, mediaUrls = []) => {
  if (!content) return [];
  const parts = content.split('\n\n');
  const blocks = [];
  let mediaIdx = 0;
  parts.forEach((part) => {
    const m = part.match(/^\[IMAGE: (.+)\]$/);
    if (m && mediaIdx < (mediaUrls || []).length) {
      blocks.push({ type: 'image', url: mediaUrls[mediaIdx], caption: m[1] === 'Photo' ? '' : m[1] });
      mediaIdx++;
    } else if (part.trim()) {
      blocks.push({ type: 'text', content: part });
    }
  });
  // Append any unreferenced trailing images
  while (mediaIdx < (mediaUrls || []).length) {
    blocks.push({ type: 'image', url: mediaUrls[mediaIdx], caption: '' });
    mediaIdx++;
  }
  return blocks;
};

const formatDate = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ts; }
};

const NewsArticleDetailDialog = ({ open, onOpenChange, post }) => {
  // Hooks must run regardless of post being null, so define before any early return.
  const { open: openLightbox, Lightbox } = useMediaLightbox();
  if (!post) return null;
  const platformKey = (post.platform || '').toLowerCase();
  const accent = NEWS_PLATFORM_COLORS[platformKey] || '#1f2937';
  const blocks = parseBlocks(post.content, post.media_urls);
  const heroUrl = post.media_urls?.[0];
  const allMedia = post.media_urls || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {Lightbox}
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 bg-card border border-border"
        data-testid="mp-news-detail-dialog"
      >
        {/* Hero image (if any) */}
        {heroUrl && (
          <LongPressMediaTile
            index={0}
            onActivate={(i) => openLightbox(allMedia, i)}
            className="w-full h-64 sm:h-80 bg-muted relative overflow-hidden block"
          >
            <img
              src={resolveUrl(heroUrl)}
              alt={post.title || 'Article hero'}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 flex items-center gap-2">
              <div
                className="px-2 py-1 rounded text-white text-[10px] uppercase font-bold tracking-wider flex items-center gap-1.5"
                style={{ backgroundColor: accent }}
              >
                <Newspaper className="w-3 h-3" />
                {post.platform}
              </div>
              {post.category && (
                <Badge className="bg-black/60 text-white border-0 text-[10px] uppercase">
                  {post.category}
                </Badge>
              )}
            </div>
          </LongPressMediaTile>
        )}

        <div className="px-6 sm:px-10 py-6 space-y-6">
          <DialogHeader className="space-y-3 text-left">
            {!heroUrl && (
              <div className="flex items-center gap-2">
                <div
                  className="px-2 py-1 rounded text-white text-[10px] uppercase font-bold tracking-wider flex items-center gap-1.5"
                  style={{ backgroundColor: accent }}
                >
                  <Newspaper className="w-3 h-3" />
                  {post.platform}
                </div>
                {post.category && (
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {post.category}
                  </Badge>
                )}
              </div>
            )}
            <DialogTitle className={`text-2xl sm:text-3xl font-bold leading-tight text-foreground ${!post.title ? 'sr-only' : ''}`}>
              {post.title || `${post.platform || 'Post'} · ${post.author?.name || 'Post'}`}
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {post.author?.name && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" /> By {post.author.name}
                </span>
              )}
              {post.timestamp && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {formatDate(post.timestamp)}
                </span>
              )}
              {post.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {post.location}
                </span>
              )}
              {post.category && !heroUrl && (
                <span className="flex items-center gap-1">
                  <Tag className="w-3 h-3" /> {post.category}
                </span>
              )}
            </div>
          </DialogHeader>

          <article className="prose prose-sm sm:prose-base dark:prose-invert max-w-none">
            {blocks.length === 0 ? (
              <p className="text-muted-foreground italic">No content available.</p>
            ) : (
              blocks.map((block, i) => {
                if (block.type === 'text') {
                  return (
                    <p
                      key={i}
                      className="text-foreground leading-relaxed whitespace-pre-wrap text-[15px] sm:text-base"
                    >
                      {block.content}
                    </p>
                  );
                }
                // image block (skip the hero we already showed)
                if (i === 0 && block.url === heroUrl) return null;
                const mediaIdx = allMedia.indexOf(block.url);
                return (
                  <figure key={i} className="my-4">
                    <LongPressMediaTile
                      index={mediaIdx >= 0 ? mediaIdx : 0}
                      onActivate={(idx) => openLightbox(allMedia, idx)}
                      className="block"
                    >
                      <img
                        src={resolveUrl(block.url)}
                        alt={block.caption || ''}
                        className="w-full rounded-lg border border-border"
                      />
                    </LongPressMediaTile>
                    {block.caption && (
                      <figcaption className="text-xs text-muted-foreground mt-1 italic">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              })
            )}
          </article>

          {post.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-4 border-t border-border">
              {post.hashtags.map((h, i) => (
                <Badge key={i} variant="outline" className="text-[10px]">
                  #{h}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsArticleDetailDialog;
